/**
 * BranchNetworkPartnerPostbackPage — configure postback template, event mapping,
 * and view recent postback events ingested from an external partner network.
 *
 * Partner-facing copy uses "Deep Link Network" / "Mobile Attribution Vendor".
 * The word "Branch" (as a brand) is intentionally avoided in body copy — the
 * internal slug is a routing concept, not a vendor endorsement.
 *
 * GREEN-only accent palette: green-700 (titles), green-600 (cta), zinc neutrals.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	usePostbackEventsList,
	usePostbackEventCreate,
} from "./hooks";
import {
	renderPostbackTemplate,
	type PostbackEventType,
	type PostbackTemplate,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL =
	"text-xs font-medium uppercase tracking-wide text-zinc-600";
const PILL =
	"inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800";

const EVENT_TYPE_CHOICES: PostbackEventType[] = [
	"conversion",
	"click",
	"install",
	"purchase",
];

const DEFAULT_MAPPING: Array<{ placeholder: string; field: string }> = [
	{ placeholder: "{click_id}", field: "click_id" },
	{ placeholder: "{device_id}", field: "device_id" },
	{ placeholder: "{payout}", field: "payout" },
];

function formatTimestamp(sec: number): string {
	try {
		const d = new Date(sec * 1000);
		return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
	} catch {
		return String(sec);
	}
}

export function BranchNetworkPartnerPostbackPage() {
	// Config form state — template the partner network should POST to.
	const [partnerId, setPartnerId] = useState("");
	const [branchId, setBranchId] = useState("");
	const [eventType, setEventType] = useState<PostbackEventType>("conversion");
	const [eventId, setEventId] = useState("");
	const [mapping, setMapping] = useState(DEFAULT_MAPPING);

	// Filter state for the recent-events feed.
	const [filterPartner, setFilterPartner] = useState("");
	const [filterType, setFilterType] = useState<PostbackEventType | "">("");

	const listQuery = usePostbackEventsList({
		partner_id: filterPartner || undefined,
		event_type: filterType || undefined,
	});
	const createMut = usePostbackEventCreate();

	const template: PostbackTemplate = useMemo(() => ({
		base_url: "/api/v1/branch-network-partner-postback",
		partner_id: partnerId || "{partner_id}",
		branch_id: branchId || "{branch_id}",
		event_type: eventType,
		mapping: Object.fromEntries(
			mapping
				.filter((m) => m.placeholder && m.field)
				.map((m) => [m.placeholder, m.field]),
		),
	}), [partnerId, branchId, eventType, mapping]);

	const previewUrl = useMemo(
		() => renderPostbackTemplate(template),
		[template],
	);

	const handleSend = (e: FormEvent) => {
		e.preventDefault();
		if (!partnerId.trim() || !branchId.trim() || !eventId.trim()) return;
		createMut.mutate(
			{
				event_id: eventId.trim(),
				partner_id: partnerId.trim(),
				branch_id: branchId.trim(),
				event_type: eventType,
			},
			{ onSuccess: () => setEventId("") },
		);
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="branch-network-partner-postback-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Attribution / Postback Ingestion
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Deep Link Network Postback
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Configure the postback URL a Mobile Attribution Vendor will call when a
					deep-link click converts. Map vendor placeholders to internal payload
					fields, then inspect the live event feed below.
				</p>
			</header>

			<section className={CARD} aria-label="Postback template configuration">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						1. Postback template
					</h2>
					<span className={PILL}>idempotent on event_id</span>
				</div>
				<form
					onSubmit={handleSend}
					className="mt-4 grid gap-4 sm:grid-cols-2"
					data-testid="branch-network-partner-postback-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Partner / Vendor ID</span>
						<input
							type="text"
							className={INPUT}
							placeholder="vendor-abc"
							value={partnerId}
							onChange={(e) => setPartnerId(e.currentTarget.value)}
							data-testid="branch-network-partner-postback-partner-input"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Tenant Branch ID</span>
						<input
							type="text"
							className={INPUT}
							placeholder="branch-42"
							value={branchId}
							onChange={(e) => setBranchId(e.currentTarget.value)}
							data-testid="branch-network-partner-postback-branch-input"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Event type</span>
						<select
							className={INPUT}
							value={eventType}
							onChange={(e) => setEventType(e.currentTarget.value as PostbackEventType)}
							data-testid="branch-network-partner-postback-eventtype-input"
						>
							{EVENT_TYPE_CHOICES.map((t) => (
								<option key={t} value={t}>{t}</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Test event_id</span>
						<input
							type="text"
							className={INPUT}
							placeholder="evt_0001"
							value={eventId}
							onChange={(e) => setEventId(e.currentTarget.value)}
							data-testid="branch-network-partner-postback-eventid-input"
						/>
					</label>
					<div className="sm:col-span-2 flex flex-col gap-2">
						<span className={LABEL}>Rendered postback URL</span>
						<code
							className="block overflow-x-auto rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700"
							data-testid="branch-network-partner-postback-preview"
						>
							{previewUrl}
						</code>
					</div>
					<div className="sm:col-span-2 flex justify-end">
						<button
							type="submit"
							className={BTN}
							disabled={
								createMut.isPending ||
								!partnerId.trim() ||
								!branchId.trim() ||
								!eventId.trim()
							}
							data-testid="branch-network-partner-postback-submit"
						>
							{createMut.isPending ? "Saving." : "Send test event"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="branch-network-partner-postback-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Event field mapping">
				<h2 className="text-base font-semibold text-zinc-900">
					2. Event field mapping
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Map vendor placeholders (what the Mobile Attribution Vendor substitutes
					into the URL) to internal payload fields.
				</p>
				<ul
					className="mt-3 flex flex-col gap-2"
					data-testid="branch-network-partner-postback-mapping-list"
				>
					{mapping.map((row, idx) => (
						<li
							key={`${row.placeholder}-${idx}`}
							className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
						>
							<input
								className={INPUT}
								value={row.placeholder}
								onChange={(e) => {
									const next = mapping.slice();
									next[idx] = { ...row, placeholder: e.currentTarget.value };
									setMapping(next);
								}}
								placeholder="{click_id}"
								data-testid={`branch-network-partner-postback-mapping-placeholder-${idx}`}
							/>
							<input
								className={INPUT}
								value={row.field}
								onChange={(e) => {
									const next = mapping.slice();
									next[idx] = { ...row, field: e.currentTarget.value };
									setMapping(next);
								}}
								placeholder="click_id"
								data-testid={`branch-network-partner-postback-mapping-field-${idx}`}
							/>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									const next = mapping.slice();
									next.splice(idx, 1);
									setMapping(next);
								}}
								data-testid={`branch-network-partner-postback-mapping-remove-${idx}`}
							>
								Remove
							</button>
						</li>
					))}
					<li>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() =>
								setMapping([...mapping, { placeholder: "", field: "" }])
							}
							data-testid="branch-network-partner-postback-mapping-add"
						>
							+ Add mapping
						</button>
					</li>
				</ul>
			</section>

			<section className={CARD} aria-label="Recent postback events">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<h2 className="text-base font-semibold text-zinc-900">
						3. Recent events
					</h2>
					<div className="flex flex-wrap items-center gap-2">
						<input
							className={INPUT + " w-40"}
							placeholder="Filter partner_id"
							value={filterPartner}
							onChange={(e) => setFilterPartner(e.currentTarget.value)}
							data-testid="branch-network-partner-postback-filter-partner"
						/>
						<select
							className={INPUT + " w-40"}
							value={filterType}
							onChange={(e) =>
								setFilterType(e.currentTarget.value as PostbackEventType | "")
							}
							data-testid="branch-network-partner-postback-filter-type"
						>
							<option value="">All types</option>
							{EVENT_TYPE_CHOICES.map((t) => (
								<option key={t} value={t}>{t}</option>
							))}
						</select>
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="branch-network-partner-postback-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="branch-network-partner-postback-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="branch-network-partner-postback-empty"
					>
						No events yet. Once the vendor fires a postback, it appears here.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="branch-network-partner-postback-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
								data-testid={`branch-network-partner-postback-row-${row.id}`}
							>
								<div className="flex flex-col gap-0.5">
									<span className="font-mono text-xs text-zinc-700">
										{row.event_id}
									</span>
									<span className="text-xs text-zinc-500">
										{row.partner_id} → tenant branch {row.branch_id}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<span className={PILL}>{row.event_type}</span>
									<span className="font-mono text-[11px] text-zinc-500">
										{formatTimestamp(row.created_at)}
									</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default BranchNetworkPartnerPostbackPage;
